import { CSS } from '@nextui-org/react';
import React, { Key, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ChevronIcon } from '../../assets';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { recalculateMaxHeight } from '../../utils/Dropdown';
import { Checkbox } from '..';
import { Radio } from '../';
import HelpTooltip from '../HelpTooltip';
import ActionModal, { ActionModalComponentProps } from '../Modal/ActionModal/ActionModal';
import {
  BottomSheetContainer,
  BottomSheetItemSecondaryText,
  BottomSheetItemText,
  Container,
  DropDownButton,
  DropDownButtonContent,
  DropdownError,
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemText,
  DropDownMenu,
  DropDownMenuItem,
  SearchInputContainer,
  StyledAvatar,
  StyledDropdown,
  StyledSearchInput,
  Title,
} from './Dropdown.styles';

interface ModalSelectionComponentProps extends ActionModalComponentProps {
  items: DropdownItemProps[];
  searchPlaceholder?: string;
  showMobileSearch?: boolean;
  selected: Set<Key>;
  disablePadding?: boolean;
  onRadioGroupChange?: (selection: Set<Key>) => void;
}

const ModalSelectionComponent = React.forwardRef(
  (
    {
      items,
      searchPlaceholder,
      showMobileSearch,
      selected,
      disablePadding,
      onRadioGroupChange,
      onFinish,
    }: ModalSelectionComponentProps,
    ref,
  ) => {
    const [searchedItems, setSearchedItems] = React.useState(items);
    const [selectedLocal, setSelectedLocal] = React.useState(selected);
    const [first] = selectedLocal;

    useImperativeHandle(ref, () => ({
      submit: () => {
        onFinish?.();
      },
    }));

    const handleTextChange = (text: string) => {
      // Update search text
      const filteredItems = items?.filter((item) => item.text.toLowerCase().includes(text.toLowerCase()));
      setSearchedItems(filteredItems);
    };

    const handleRadioGroupChange = (value) => {
      setSelectedLocal(new Set<Key>([value]));
      onRadioGroupChange?.(value);
    };

    return (
      <>
        {showMobileSearch && (
          <SearchInputContainer $disablePadding={disablePadding}>
            <StyledSearchInput onTextChange={handleTextChange} placeholder={searchPlaceholder} />
          </SearchInputContainer>
        )}
        <BottomSheetContainer $isSearchEnabled={showMobileSearch} $disablePadding={disablePadding}>
          <Radio.Group value={first} onChange={handleRadioGroupChange} aria-label="modalRadioGroup">
            {searchedItems.map((item, index) => (
              <Radio size="lg" style={{ flex: 1 }} value={getItemKey(item)} key={`radioItem_${index}`}>
                <BottomSheetItemText $isSelected={selectedLocal.has(getItemKey(item))}>{item.text}</BottomSheetItemText>
                <BottomSheetItemSecondaryText $isSelected={selectedLocal.has(getItemKey(item))}>
                  {item.description}
                </BottomSheetItemSecondaryText>
              </Radio>
            ))}
          </Radio.Group>
        </BottomSheetContainer>
      </>
    );
  },
);

export interface DropdownItemProps {
  key?: string;
  text: string;
  value?: any;
  description?: string;
  selected?: boolean;
  imageUrl?: string;
}

export const getItemKey = (item: DropdownItemProps) => item?.key ?? item?.value ?? item?.text;

export interface DropDownProps extends React.ComponentPropsWithoutRef<typeof Container> {
  title?: string;
  titleColor?: string;
  mobileModalTitle?: string;
  alwaysShowTitle?: boolean; // In order to render the space that the title will occupy
  items: DropdownItemProps[];
  selectedIndex?: number;
  placeholder?: string;
  multiselect?: boolean;
  helpText?: string;
  disabled?: boolean;
  onSelectionChange?: (selection: number | number[]) => void;
  bordered?: boolean;
  placement?: string;
  buttonPadding?: number;
  disallowEmptySelection?: boolean;
  itemTemplate?: (item: DropdownItemProps) => ReactNode;
  disabledKeys?: string[];
  minWidthButton?: boolean;
  staticMenuWidth?: boolean;
  buttonFontSize?: string;
  buttonFontWeight?: number;
  noPaddingLeft?: boolean;
  expandWithoutBorder?: boolean;
  buttonMaxHeight?: string;
  fullWidth?: boolean;
  buttonSidesPadding?: number;
  buttonCSS?: any;
  displayRadioIcon?: boolean;
  menuItemTextCSS?: CSS;
  menuItemCSS?: CSS;
  menuContainerCSS?: CSS;
  noIconRight?: boolean;
  iconLeft?: ReactNode;
  fixedButtonLabel?: string; // If provided, the button will always show this label, regardless of the selected value.
  disableSvgAnimation?: boolean;
  showMobileSearch?: boolean;
  searchPlaceholder?: string;
  error?: string;
  zIndexBottomSheet?: number;
  customBottomSheetHandler?: (content: ReactNode) => void;
}

const Dropdown = ({
  items,
  alwaysShowTitle,
  title,
  titleColor,
  mobileModalTitle,
  selectedIndex,
  placeholder,
  multiselect,
  helpText,
  disabled,
  onSelectionChange,
  bordered = true,
  placement = 'bottom-left',
  buttonSidesPadding,
  disallowEmptySelection,
  itemTemplate,
  disabledKeys,
  minWidthButton,
  staticMenuWidth,
  buttonFontSize,
  buttonFontWeight,
  noPaddingLeft,
  expandWithoutBorder,
  buttonMaxHeight,
  fullWidth,
  buttonCSS,
  displayRadioIcon = true,
  menuItemTextCSS,
  menuItemCSS,
  menuContainerCSS,
  noIconRight,
  iconLeft,
  fixedButtonLabel,
  disableSvgAnimation,
  showMobileSearch,
  searchPlaceholder,
  error,
  zIndexBottomSheet,
  customBottomSheetHandler,
  ...rest
}: DropDownProps) => {
  const [selected, setSelected] = React.useState(
    new Set<Key>(selectedIndex !== undefined ? [getItemKey(items?.[selectedIndex])] : []),
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const [maxHeight, setMaxHeight] = useState('20rem');

  const [selectedIndexInitial, setSelectedIndexInitial] = useState(selectedIndex);
  if (selectedIndex !== undefined && selectedIndex !== selectedIndexInitial) {
    setSelectedIndexInitial(selectedIndex);
    setSelected(new Set<Key>([getItemKey(items?.[selectedIndex])]));
  }

  // Reset values if selectedIndex is undefined
  React.useEffect(() => {
    if (selectedIndex === undefined) {
      setSelectedIndexInitial(undefined);
      setSelected(new Set<Key>([]));
    }
  }, [selectedIndex]);

  const buttonRef = useRef<HTMLElement>();
  const [buttonWidth, setButtonWidth] = useState(0);
  const mobileModalRef = useRef(null);

  const selectedValue = React.useMemo(
    () =>
      Array.from(selected)
        .map((key) => items.find((item) => getItemKey(item) === key)?.text)
        .join(', '),
    [items, selected],
  );
  const onLocalSelectionChange = (keys: 'all' | Set<Key>) => {
    if (keys === 'all') return;
    if (keys.size === 0 && !multiselect) return;
    setSelected(keys);
    const selectedIndexes = Array.from(keys || []).map((key) =>
      items.findIndex((item) => getItemKey(item).toString() === key),
    );
    onSelectionChange?.(multiselect ? selectedIndexes : selectedIndexes[0]);
  };

  React.useLayoutEffect(() => {
    function updateSize() {
      setButtonWidth(buttonRef.current.offsetWidth);
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const modalSelectionComponentProps = {
    items,
    searchPlaceholder,
    showMobileSearch,
    selected,
    onRadioGroupChange: (value) => {
      setSelected(new Set<Key>([value]));
      const selectedIndexes = Array.from([value] || []).map((key) =>
        items.findIndex((item) => getItemKey(item) === key),
      );
      onSelectionChange?.(multiselect ? selectedIndexes : selectedIndexes[0]);
      mobileModalRef.current?.close();
    },
  };

  const onDropdownButtonPressed = () => {
    if (inMobile(windowWidth)) {
      if (customBottomSheetHandler) {
        // timeout to avoid UI issues related to useRadioGroup hook
        setTimeout(() => {
          customBottomSheetHandler?.(<ModalSelectionComponent disablePadding {...modalSelectionComponentProps} />);
        }, 100);
      } else {
        mobileModalRef.current?.open();
      }
    }
  };

  const handleOnOpenChange = (isOpen: boolean) => {
    if (!inMobile(windowWidth)) {
      setIsOpen(isOpen);
      setTimeout(() => {
        setMaxHeight(recalculateMaxHeight(false));
        // Wait for the dropdown to open
      }, 100);
    }
  };

  useEffect(() => {
    const calculateMaxHeightOnResize = () => {
      setMaxHeight(recalculateMaxHeight(false));
    };

    // Listen for changes in the popover and recalculate the max height
    window.addEventListener('resize', calculateMaxHeightOnResize);
    window.addEventListener('scroll', calculateMaxHeightOnResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', calculateMaxHeightOnResize);
      window.removeEventListener('scroll', calculateMaxHeightOnResize);
    };
  }, []);

  return (
    <Container $fullWidth={fullWidth} {...rest}>
      {(title || alwaysShowTitle) && (
        <Title $alwaysShowTitle={alwaysShowTitle} $color={titleColor}>
          {title || ' '}
          {helpText && <HelpTooltip $helpText={helpText} />}
        </Title>
      )}
      <StyledDropdown
        isDisabled={disabled}
        placement={placement}
        onOpenChange={handleOnOpenChange}
        error={error}
        {...rest}
      >
        <DropDownButton
          className="ddbutton"
          disabled={disabled}
          ref={buttonRef}
          iconRight={noIconRight ? undefined : <ChevronIcon />}
          $buttonWidth={minWidthButton ? buttonWidth : 'unset'}
          bordered={bordered}
          $buttonSidesPadding={buttonSidesPadding}
          $fontSize={buttonFontSize}
          $fontWeight={buttonFontWeight}
          $noPaddingLeft={noPaddingLeft}
          $expandWithoutBorder={expandWithoutBorder}
          $maxHeight={buttonMaxHeight}
          $buttonCSS={buttonCSS}
          onPress={onDropdownButtonPressed}
          $isOpen={isOpen}
          $disableSvgAnimation={disableSvgAnimation}
          $isPlaceholder={!selectedValue}
          color={error ? 'error' : 'default'}
          error={error}
        >
          <DropDownButtonContent>
            {/* If not multiselect, we give the option to show an avatar at the left of the selected option. */}
            {!multiselect && items.find((item) => getItemKey(item) === selected?.values()?.next()?.value)?.imageUrl && (
              <StyledAvatar src={items.find((item) => getItemKey(item) === selected.values().next().value).imageUrl} />
            )}
            {iconLeft}
            {fixedButtonLabel ?? (selectedValue || placeholder || '')}
          </DropDownButtonContent>
        </DropDownButton>
        {!inMobile(windowWidth) ? (
          <DropDownMenu
            width={staticMenuWidth ? undefined : buttonWidth}
            selectionMode={multiselect ? 'multiple' : 'single'}
            disallowEmptySelection={multiselect ? false : disallowEmptySelection}
            selectedKeys={selected}
            disabledKeys={disabledKeys}
            onSelectionChange={onLocalSelectionChange}
            menuContainerCSS={menuContainerCSS}
            $maxHeight={maxHeight}
          >
            {items.map((item, index) => (
              <DropDownMenuItem
                css={menuItemCSS}
                key={getItemKey(item)}
                textValue={item.text}
                dividerWeight="extrabold"
              >
                {itemTemplate ? (
                  <>{itemTemplate(item)}</>
                ) : (
                  <DropDownItemContainer>
                    {multiselect ? (
                      <Checkbox isSelected={selected.has(getItemKey(item))} aria-label={`desktopCheckbox_${index}`} />
                    ) : (
                      <>
                        {displayRadioIcon ? (
                          <Radio.Group
                            value={selected.has(getItemKey(item)) ? item.text : undefined}
                            aria-label="desktopRadioGroup"
                          >
                            <Radio value={item.text} aria-label={`desktopRadio_${index}`} />
                          </Radio.Group>
                        ) : null}
                      </>
                    )}
                    <DropDownItemText $isSelected={selected.has(getItemKey(item))} css={menuItemTextCSS}>
                      <div>{item.text}</div>
                    </DropDownItemText>
                    {item.description && <DropDownItemDescription>{item.description}</DropDownItemDescription>}
                  </DropDownItemContainer>
                )}
              </DropDownMenuItem>
            ))}
          </DropDownMenu>
        ) : (
          <></>
        )}
      </StyledDropdown>
      {error && <DropdownError>{error}</DropdownError>}
      <ActionModal
        ref={mobileModalRef}
        title={mobileModalTitle}
        Component={ModalSelectionComponent}
        componentProps={modalSelectionComponentProps}
        hideFooter
        noPadding
        zIndexBottomSheet={zIndexBottomSheet}
      />
    </Container>
  );
};

export default Dropdown;
